import { defineStore } from 'pinia';
import type { EntityId } from '../api/entityId';
import {
  CommandsService,
  QueriesService,
  type UnreadCommentResponse
} from '../api/generated';
import { getErrorMessage } from './session';

interface NotificationsState {
  unreadComments: UnreadCommentResponse[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

let unreadLoadPromise: Promise<void> | null = null;
let unreadReloadRequested = false;
let stateGeneration = 0;
let readMutationVersion = 0;

export const useNotificationsStore = defineStore('notifications', {
  state: (): NotificationsState => ({
    unreadComments: [],
    unreadCount: 0,
    loading: false,
    error: null
  }),
  getters: {
    hasUnread: state => state.unreadCount > 0
  },
  actions: {
    async loadUnread(): Promise<void> {
      unreadReloadRequested = true;
      if (unreadLoadPromise != null) {
        await unreadLoadPromise;
        return;
      }

      const generation = stateGeneration;
      const pendingLoad = this.runLoadUnread(generation);
      unreadLoadPromise = pendingLoad;
      try {
        await pendingLoad;
      } finally {
        if (unreadLoadPromise === pendingLoad) {
          unreadLoadPromise = null;
        }
      }
    },
    async runLoadUnread(generation: number): Promise<void> {
      this.loading = true;
      this.error = null;
      try {
        while (unreadReloadRequested && generation === stateGeneration) {
          unreadReloadRequested = false;
          const requestReadMutationVersion = readMutationVersion;
          const response = await QueriesService.queryGetUnreadCommentsQuery({ requestBody: {} });
          if (generation !== stateGeneration) {
            return;
          }

          if (requestReadMutationVersion !== readMutationVersion) {
            unreadReloadRequested = true;
            continue;
          }

          this.unreadComments = response.comments;
          this.unreadCount = Number(response.count);
        }
      } catch (error) {
        if (generation === stateGeneration) {
          this.error = getErrorMessage(error, 'Unable to load unread comments.');
        }
      } finally {
        if (generation === stateGeneration) {
          this.loading = false;
        }
      }
    },
    async markDocumentRead(documentId: EntityId): Promise<void> {
      const generation = stateGeneration;
      try {
        await CommandsService.commandMarkDocumentCommentsReadCommand({
          requestBody: { documentId }
        });
        if (generation !== stateGeneration) {
          return;
        }

        readMutationVersion++;
        const remaining = this.unreadComments.filter(comment => String(comment.documentId) !== String(documentId));
        this.unreadCount = remaining.length;
        this.unreadComments = remaining;
      } catch (error) {
        if (generation === stateGeneration) {
          this.error = getErrorMessage(error, 'Unable to mark comments as read.');
        }
      }
    },
    reset(): void {
      stateGeneration++;
      readMutationVersion++;
      unreadReloadRequested = false;
      unreadLoadPromise = null;
      this.unreadComments = [];
      this.unreadCount = 0;
      this.loading = false;
      this.error = null;
    }
  }
});
